import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { get, isEmpty, isNil, snakeCase } from 'lodash';

import CheckboxFormik from '~/components/CheckboxFormik';
import { localeDetails } from '~/components/CmsMain/localeGlobals';
import { filterEnabledContactRoles } from '~/components/communications/ContactUtils';
import WithConfirm from '~/components/ConfirmModal';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InnerCard from '~/components/core/Cards/InnerCard';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import TextField from '~/components/core/Molecules/Fields/TextField';
import TooltipIcon from '~/components/core/TooltipIcon';
import { InfoIcon } from '~/components/icons';
import useOrganization from '~/components/OrganizationContext';
import {
  CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY,
  CLIVE_EXCLUDED_FIELD_KEY,
  useCliveConfigOptions,
} from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import {
  CONTACT_API_SUFFIX,
  CONTACT_CONFIG_SUFFIX,
} from '~/components/SystemConfiguration/FnolConfiguration/constants';
import {
  buildCategoryLimitOptions,
  usePolicyCategoryOptions,
} from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/PolicyCategoriesOptions';
import TextFieldFormik from '~/components/TextFieldFormik';
import { COUNTRY_TO_STATE_MAP } from '~/Types';
import { isLocaleRegionIsUK, isLocaleRegionIsUs } from '~/Utils/regionUtils';

import copies from '../copies.json';
import HiddenIndication from '../HiddenIndication/HiddenIndication';
import { isNewRow } from '../newRowIdentity';
import OptionsCard from '../OptionsCard/OptionsCard';

import ConfigurationFieldType from './ConfigurationFieldType';

const isFieldGeoSpecificNotAllowed = (geoSpecific, fieldType) => {
  // us_state_select is country-aware at runtime regardless of stored geo_specific.
  // Mirror that here so legacy US-tagged configs render in SysConfig for AU orgs.
  if (fieldType === 'us_state_select') {
    return !COUNTRY_TO_STATE_MAP[localeDetails.locale.region];
  }

  if (!geoSpecific) return false;

  if (geoSpecific === 'US' && !isLocaleRegionIsUs()) {
    return true;
  }

  if (geoSpecific === 'GB' && !isLocaleRegionIsUK()) {
    return true;
  }
};

const getActions = ({ isActive = false, disabledCollapse = false, actions = undefined }) => (
  <div className="flex items-center">
    <HiddenIndication isActive={isActive && !disabledCollapse} />
    {actions}
  </div>
);

const getDisplayKey = (values, id, isConfiguredField, fieldId) => {
  const displayKey = isConfiguredField ? get(values, `${id}.id`, '') : fieldId;
  return displayKey.endsWith(CONTACT_CONFIG_SUFFIX)
    ? displayKey.replace(CONTACT_CONFIG_SUFFIX, CONTACT_API_SUFFIX)
    : displayKey;
};

const FieldConfigurationCard = ({
  idPrefix,
  fieldId,
  forceRequired = false,
  isConfiguredField = false,
  disabledCollapse = false,
  actions = undefined,
  forceDisabled = false,
  infoTooltipText,
  backgroundColor,
  disableCategories = false,
  hideCategories = false,
  disableType = false,
  disableAdd = false,
  withoutRequiredForClaimClosing = false,
  withoutRequired = false,
  withShowInInvolved = false,
  withIncludeInManagementPreview = false,
  requiredLabel,
  configuredFieldIdToOriginalIndex = null,
  fieldIdParentArray = null,
  disableCliveAdditionalInstructions = false,
  withRequiredConfirmationModal = true,
  defaultExpanded = false,
  excludeTypes = [],
  contactRolesEditable = false,
  withDndReorder = false,
  readOnly = false,
}) => {
  const { values, setFieldValue, initialValues } = useFormikContext();
  const policyCategoryOptions = usePolicyCategoryOptions();
  const { organizationContactRolesDict } = useOrganization();

  const id = idPrefix ? `${idPrefix}.${fieldId}` : fieldId;
  const field = get(values, id, {});
  // `readOnly` is for a row this configuration does not own (a projected global field): it locks what
  // `forceDisabled` locks and also the flags and Clive settings `forceDisabled` leaves editable.
  const isLocked = forceDisabled || readOnly;

  const originalFieldIndex = configuredFieldIdToOriginalIndex?.[field?.id];

  const wasRequired = get(initialValues, `${id}.mandatory`, false);
  const isRequired = get(values, `${id}.mandatory`, false);
  const isRequiredBeforeClose = get(values, `${id}.claim_closing_mandatory`, false);
  const isActive = get(values, `${id}.active`, false);
  const isCliveExcluded = get(values, `${id}.${CLIVE_EXCLUDED_FIELD_KEY}`, false);
  const fieldType = get(values, `${id}.type`, '');
  const isOptionsField = ['select', 'multiselect'].includes(fieldType);
  const isContactField = ['contact'].includes(fieldType);
  // location-type fields are dropped from the involved/management-preview render paths, so don't
  // offer flags that would silently do nothing.
  const showShowInInvolvedCheckbox = withShowInInvolved && isConfiguredField && fieldType !== 'location';
  const showIncludeInManagementPreviewCheckbox =
    withIncludeInManagementPreview && isConfiguredField && fieldType !== 'location';
  const geoSpecific = get(values, `${id}.geo_specific`);
  const incidentCategories = get(values, 'configuration.categories', [])?.map((category) => ({
    id: category.id,
    value: category.id,
    label: category.desc,
  }));
  // Incident categories come from the form state (live while editing); policy categories
  // are table rows fetched once per screen. One flat key namespace, grouped for display.
  const categories = buildCategoryLimitOptions(incidentCategories, policyCategoryOptions);
  const contactRoles = filterEnabledContactRoles(
    organizationContactRolesDict,
    Object.keys(organizationContactRolesDict)
  )?.map((role) => ({
    id: role,
    value: role,
    label: organizationContactRolesDict[role]?.desc,
  }));
  // A configured field is "new" when the row was added in this editing session, or - for rows that
  // predate the marker or come from a caller that does not stamp one - when its key is absent from
  // the saved set. Neither the array position nor the key is a stable identity on its own: rows are
  // keyed by position, so a DnD reorder moves a row onto a slot whose saved value belongs to a
  // different field; and while a row is new its key is re-derived from the Display Name on every
  // keystroke, so a key that grows into a saved field's key would otherwise read as saved and lock
  // the row's Key, Type and Remove with no way out. The marker is stable under both. The `||` keeps
  // every unmarked row behaving exactly as before, including the non-reorderable predefined-field
  // lists that supply no map.
  const isNewConfiguredField = isConfiguredField
    ? isNewRow(field) ||
      (configuredFieldIdToOriginalIndex ? isNil(originalFieldIndex) : isEmpty(get(initialValues, id, {})))
    : false;
  // A new row must not adopt the saved field's options: OptionsCard resolves its initial (and
  // therefore undeletable) option list from this path.
  const originalFieldId =
    !isNewConfiguredField && !isNil(originalFieldIndex) && fieldIdParentArray
      ? `${fieldIdParentArray}[${originalFieldIndex}]`
      : null;
  // The Display Name -> Key sync must stop once the user has really edited the Key, and Formik
  // `touched` cannot say that: it is set on blur, so merely clicking into the Key to look at it
  // would freeze the key mid-word (the defect that produced a saved field keyed `firs` for a
  // Display Name of "First Name"). Derive it instead - the key is still tracking the name exactly
  // while it equals snakeCase(desc), which holds by construction for a row from getNewField
  // (`Configured Field 3` -> `configured_field_3`) and stops holding the moment the user types
  // anything else into the Key.
  const keyFollowsDesc = get(values, `${id}.id`, '') === snakeCase(get(values, `${id}.desc`, ''));
  const displayKey = getDisplayKey(values, id, isConfiguredField, fieldId);
  const isDynamicField = get(values, `${id}.dynamic`, false);
  const title = !infoTooltipText ? (
    field.desc
  ) : (
    <div className="flex items-center">
      <div className="mr-12">{field.desc}</div>
      <TooltipIcon title={infoTooltipText} arrow>
        <InfoIcon size={20} />
      </TooltipIcon>
    </div>
  );

  const { showCliveConfigOptions } = useCliveConfigOptions();

  useEffect(() => {
    if (forceRequired) {
      setFieldValue(`${id}.active`, true);
      setFieldValue(`${id}.mandatory`, true);
    }
  }, [forceRequired, id, setFieldValue]);

  if (isFieldGeoSpecificNotAllowed(geoSpecific, field?.type)) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <CollapsibleWrapper
        title={title}
        subtitle={`Key: ${displayKey}`}
        backgroundColor={backgroundColor || CollapsibleWrapper.BACKGROUND_COLOR.LIST}
        actionCard
        noBorderLine
        noDivider
        actions={getActions({ isActive, disabledCollapse, actions })}
        disabled={disabledCollapse}
        withActionsContainerFullWidth={false}
        defaultState={defaultExpanded || isNewConfiguredField}
      >
        {/* In a DnD row the InnerCard's own bottom margin is trapped inside the draggable
            flex item and shows as a white strip under the card while dragging; row spacing
            comes from the list gap, so drop it here. */}
        <InnerCard className={withDndReorder ? '!mb-0' : undefined}>
          <Grid container className="flex items-center" spacing={3}>
            <Grid item xs={6}>
              <TextFieldFormik
                id={`${id}.desc`}
                label="Display Name"
                fullWidth
                disabled={isLocked || !isActive}
                // Sync the Key from the Display Name edit-driven (only while the user types),
                // not via a value-watching effect: rows are keyed by position, so a reorder
                // shuffles data between positions and a watcher reads that as an edit — firing
                // a redundant setFieldValue + full-form re-render per moved row. Typing here is
                // the only real trigger, so a reorder can never cause a key sync.
                onChange={(e) => {
                  const newDesc = e.target.value;
                  setFieldValue(`${id}.desc`, newDesc);
                  if (isNewConfiguredField && keyFollowsDesc) {
                    setFieldValue(`${id}.id`, snakeCase(newDesc));
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              {isNewConfiguredField ? (
                <TextFieldFormik id={`${id}.id`} label="Key" fullWidth />
              ) : (
                <TextField label="Key" value={displayKey} fullWidth disabled />
              )}
            </Grid>
            {!disableType && (
              <Grid item xs={6}>
                <ConfigurationFieldType
                  id={id}
                  isNewConfiguredField={isNewConfiguredField}
                  excludeTypes={excludeTypes}
                  disabled={readOnly}
                />
              </Grid>
            )}
            {!hideCategories ? (
              <Grid item xs={disableType ? 12 : 6}>
                <ArrayMultiselectFieldWithChipsFormik
                  id={`${id}.incident_sub_type_categories`}
                  label="Limit to Categories"
                  options={categories}
                  disabled={
                    forceRequired || isLocked || isDynamicField || !isActive || isEmpty(categories) || disableCategories
                  }
                />
              </Grid>
            ) : null}
            {isContactField && (
              <>
                <Grid item xs={6}>
                  <ArrayMultiselectFieldWithChipsFormik
                    id={`${id}.accepted_roles`}
                    label="Limit to Contact Roles"
                    options={contactRoles}
                    disabled={
                      forceRequired ||
                      isLocked ||
                      isDynamicField ||
                      !isActive ||
                      isEmpty(contactRoles) ||
                      (!isNewConfiguredField && !contactRolesEditable)
                    }
                  />
                </Grid>
                <Grid item xs={6} />
              </>
            )}
            <Grid item xs={12} className="MuiGrid-item !pl-0">
              {isActive && (isRequired || isRequiredBeforeClose) && !(forceRequired || isLocked) ? (
                <FsTooltip arrow title="Required Field cannot be hidden">
                  <SwitchFormik
                    id={`${id}.active`}
                    label="Show"
                    hideErrorGap
                    disabled={forceRequired || isLocked || isRequired || isRequiredBeforeClose}
                  />
                </FsTooltip>
              ) : (
                <SwitchFormik
                  id={`${id}.active`}
                  label="Show"
                  hideErrorGap
                  disabled={forceRequired || isLocked || isRequired || isRequiredBeforeClose}
                />
              )}
            </Grid>
            {!withoutRequired ? (
              <Grid item xs={6}>
                {withRequiredConfirmationModal ? (
                  <WithConfirm
                    title={copies.change_field_required_confirm.title}
                    contentText={copies.change_field_required_confirm.text}
                    primaryButtonName="YES, PROCEED"
                    shouldCloseOnPrimary
                    triggerMethod="onChange"
                    centerDialog
                    disableConfirm={isRequired || wasRequired}
                  >
                    <CheckboxFormik
                      id={`${id}.mandatory`}
                      label={requiredLabel || 'Required field for claim submission'}
                      disabled={forceRequired || isLocked || isDynamicField || !isActive}
                      onChange={() => {
                        const newMandatoryValue = !get(values, `${id}.mandatory`, false);
                        setFieldValue(`${id}.mandatory`, newMandatoryValue);
                        if (newMandatoryValue && isCliveExcluded) {
                          setFieldValue(`${id}.${CLIVE_EXCLUDED_FIELD_KEY}`, false);
                          toast.info('Clive exclusion has been turned off - mandatory fields must be sent to Clive');
                        }
                      }}
                    />
                  </WithConfirm>
                ) : (
                  <CheckboxFormik
                    id={`${id}.mandatory`}
                    label={requiredLabel || 'Required field for claim submission'}
                    disabled={forceRequired || isLocked || isDynamicField || !isActive}
                    onChange={() => {
                      const newMandatoryValue = !get(values, `${id}.mandatory`, false);
                      setFieldValue(`${id}.mandatory`, newMandatoryValue);
                      if (newMandatoryValue && isCliveExcluded) {
                        setFieldValue(`${id}.${CLIVE_EXCLUDED_FIELD_KEY}`, false);
                        toast.info('Clive exclusion has been turned off - mandatory fields must be sent to Clive');
                      }
                    }}
                  />
                )}
              </Grid>
            ) : null}
            {isConfiguredField && !withoutRequiredForClaimClosing ? (
              <Grid item xs={6}>
                <CheckboxFormik
                  id={`${id}.claim_closing_mandatory`}
                  label="Required field when claim closed"
                  disabled={readOnly || !isActive}
                  onChange={() =>
                    setFieldValue(`${id}.claim_closing_mandatory`, !get(values, `${id}.claim_closing_mandatory`, false))
                  }
                />
              </Grid>
            ) : null}
            {showShowInInvolvedCheckbox ? (
              <Grid item xs={6}>
                <CheckboxFormik
                  id={`${id}.show_in_involved`}
                  label="Show in involved"
                  disabled={readOnly || !isActive}
                  onChange={() =>
                    setFieldValue(`${id}.show_in_involved`, !get(values, `${id}.show_in_involved`, false))
                  }
                />
              </Grid>
            ) : null}
            {showIncludeInManagementPreviewCheckbox ? (
              <Grid item xs={6}>
                <CheckboxFormik
                  id={`${id}.include_in_management_preview`}
                  label="Include in management preview"
                  disabled={readOnly || !isActive}
                  onChange={() =>
                    setFieldValue(
                      `${id}.include_in_management_preview`,
                      !get(values, `${id}.include_in_management_preview`, false)
                    )
                  }
                />
              </Grid>
            ) : null}
            {fieldId === 'date_of_loss' && (
              <Grid item xs={6}>
                <CheckboxFormik
                  id={`${id}.allow_future`}
                  label="Allow future dates"
                  disabled={isLocked || !isActive}
                  onChange={() => setFieldValue(`${id}.allow_future`, !get(values, `${id}.allow_future`, false))}
                />
              </Grid>
            )}
            {isOptionsField && !isDynamicField && (
              <OptionsCard
                idPrefix={idPrefix}
                fieldId={fieldId}
                disableAdd={disableAdd}
                originalFieldId={originalFieldId}
                hideCategories={hideCategories || disableCategories}
                withDndReorder={withDndReorder}
                readOnly={readOnly}
              />
            )}
            {showCliveConfigOptions && !disableCliveAdditionalInstructions && (
              <Grid item xs={12} className="MuiGrid-item !pl-0">
                {isRequired ? (
                  <FsTooltip arrow title="Required field cannot be excluded from Clive">
                    <SwitchFormik
                      id={`${id}.${CLIVE_EXCLUDED_FIELD_KEY}`}
                      label="Exclude from Clive Auto-Fill"
                      hideErrorGap
                      disabled
                    />
                  </FsTooltip>
                ) : (
                  <SwitchFormik
                    id={`${id}.${CLIVE_EXCLUDED_FIELD_KEY}`}
                    label="Exclude from Clive Auto-Fill"
                    hideErrorGap
                    disabled={isLocked || !isActive}
                  />
                )}
              </Grid>
            )}
            {showCliveConfigOptions && !disableCliveAdditionalInstructions ? (
              <Grid item xs={12}>
                <TextFieldFormik
                  id={`${id}.${CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY}`}
                  label="Clive additional instructions"
                  fullWidth
                  multiline
                  rows={3}
                  disabled={readOnly || isCliveExcluded}
                />
              </Grid>
            ) : null}
          </Grid>
        </InnerCard>
      </CollapsibleWrapper>
    </Grid>
  );
};

FieldConfigurationCard.propTypes = {
  idPrefix: PropTypes.string,
  fieldId: PropTypes.string.isRequired,
  savedValues: PropTypes.object,
  forceRequired: PropTypes.bool,
  isConfiguredField: PropTypes.bool,
  disabledCollapse: PropTypes.bool,
  actions: PropTypes.node,
  forceDisabled: PropTypes.bool,
  infoTooltipText: PropTypes.string,
  backgroundColor: PropTypes.oneOf(Object.values(CollapsibleWrapper.BACKGROUND_COLOR)),
  disableCategories: PropTypes.bool,
  hideCategories: PropTypes.bool,
  disableType: PropTypes.bool,
  disableAdd: PropTypes.bool,
  withoutRequiredForClaimClosing: PropTypes.bool,
  withoutRequired: PropTypes.bool,
  withShowInInvolved: PropTypes.bool,
  withIncludeInManagementPreview: PropTypes.bool,
  requiredLabel: PropTypes.string,
  configuredFieldIdToOriginalIndex: PropTypes.object,
  fieldIdParentArray: PropTypes.string,
  disableCliveAdditionalInstructions: PropTypes.bool,
  withRequiredConfirmationModal: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  excludeTypes: PropTypes.arrayOf(PropTypes.string),
  contactRolesEditable: PropTypes.bool,
  withDndReorder: PropTypes.bool,
  readOnly: PropTypes.bool,
};

FieldConfigurationCard.BACKGROUND_COLOR = CollapsibleWrapper.BACKGROUND_COLOR;

export default FieldConfigurationCard;
